import { useState, useRef, useEffect, useCallback } from 'react';
import { PositionCoords } from '@/shared/types/tooltip';
import { computeTooltipCoords } from '../utils/tooltipPosition';

export const useTooltip = (
  position: string = 'top',
  alignment: string = 'center',
  absolute: boolean = false,
  delay: number = 0,
  disabled: boolean = false
) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<PositionCoords>({ top: 0, left: 0 });
  const [tooltipDimensions, setTooltipDimensions] = useState({ width: 0, height: 0 });
  const [adjustedPosition, setAdjustedPosition] = useState<string>(position);

  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const positionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastTriggerPositionRef = useRef<{ top: number; left: number } | null>(null);

  const lastCoordsRef = useRef<PositionCoords>({ top: 0, left: 0 });
  const loopDetectionRef = useRef<{ count: number; lastTime: number }>({ count: 0, lastTime: 0 });

  const closeTooltip = useCallback(() => {
    setIsVisible(false);
    setCoords({ top: 0, left: 0 });
    setAdjustedPosition(position);
    lastTriggerPositionRef.current = null;
    loopDetectionRef.current.count = 0;
    loopDetectionRef.current.lastTime = 0;
    lastCoordsRef.current = { top: 0, left: 0 };
  }, [position]);

  const checkTriggerPosition = useCallback(() => {
    if (!triggerRef.current || !isVisible) return;

    const currentRect = triggerRef.current.getBoundingClientRect();
    const currentPosition = { top: currentRect.top, left: currentRect.left };

    if (lastTriggerPositionRef.current) {
      const lastPosition = lastTriggerPositionRef.current;
      const threshold = 2;

      if (
        Math.abs(currentPosition.top - lastPosition.top) > threshold ||
        Math.abs(currentPosition.left - lastPosition.left) > threshold
      ) {
        closeTooltip();
        return;
      }
    }

    lastTriggerPositionRef.current = currentPosition;
  }, [isVisible, closeTooltip]);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect() ?? null;

    const result = computeTooltipCoords({
      position,
      alignment,
      absolute,
      triggerRect,
      tooltipRect,
      containerRect,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });

    if (result.status === 'noop') return;

    if (result.status === 'close') {
      closeTooltip();
      return;
    }

    if (result.flippedAdjustedPosition !== undefined) {
      setAdjustedPosition(result.flippedAdjustedPosition);
    }

    const { top, left } = result.coords;

    const currentTime = Date.now();
    const timeDiff = currentTime - loopDetectionRef.current.lastTime;

    if (timeDiff < 100) {
      loopDetectionRef.current.count++;

      if (loopDetectionRef.current.count > 5) {
        closeTooltip();
        return;
      }
    } else {
      loopDetectionRef.current.count = 0;
    }

    loopDetectionRef.current.lastTime = currentTime;
    lastCoordsRef.current = { top, left };

    setCoords({ top, left });
  }, [position, alignment, absolute, closeTooltip]);

  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      requestAnimationFrame(() => {
        calculatePosition();
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          lastTriggerPositionRef.current = { top: rect.top, left: rect.left };
        }
      });

      positionCheckIntervalRef.current = setInterval(checkTriggerPosition, 100);
    } else {
      if (positionCheckIntervalRef.current) {
        clearInterval(positionCheckIntervalRef.current);
        positionCheckIntervalRef.current = null;
      }
    }

    return () => {
      if (positionCheckIntervalRef.current) {
        clearInterval(positionCheckIntervalRef.current);
        positionCheckIntervalRef.current = null;
      }
    };
  }, [isVisible, calculatePosition, checkTriggerPosition]);

  useEffect(() => {
    if (!triggerRef.current) return;

    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && isVisible) {
            closeTooltip();
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '8px',
      }
    );

    intersectionObserverRef.current.observe(triggerRef.current);

    return () => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }
    };
  }, [isVisible, closeTooltip]);

  useEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => {
      if (!absolute) {
        closeTooltip();
      }
    };

    const handleResize = () => {
      closeTooltip();
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isVisible, absolute, closeTooltip]);

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, delay);
    } else {
      setIsVisible(true);
    }
  }, [disabled, delay]);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
    setCoords({ top: 0, left: 0 });
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (positionCheckIntervalRef.current) {
        clearInterval(positionCheckIntervalRef.current);
      }
    };
  }, []);

  return {
    isVisible,
    coords,
    tooltipDimensions,
    adjustedPosition,
    triggerRef,
    tooltipRef,
    containerRef,
    handleMouseEnter,
    handleMouseLeave,
    calculatePosition,
  };
};
